import React from "react";
import { CircularProgress } from "@mui/material";
import { Delete } from "@mui/icons-material";
import type { ComputerWithHealthResponseDto } from "../../../../typescript-fetch-sdk/api";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { normalizeStatus, formatDateTime, formatTimeElapsed } from "./utils";
import {
  ComputerListSection,
  ComputerTable,
  TableHeader,
  TableRow,
  ComputerName,
  LastCheckTime,
  ElapsedTime,
  StatusBadge,
  StatusDot,
  ActionsButton,
  LoadingContainer,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  HiddenOnMobile,
} from "./styled";

export type StatusFilter = "all" | "healthy" | "stale" | "inactive";

export const ComputerList: React.FC<{
  computers: ComputerWithHealthResponseDto[];
  loading: boolean;
  searchTerm: string;
  statusFilter: StatusFilter;
  onComputerClick: (computer: ComputerWithHealthResponseDto) => void;
  onDeleteClick?: (computer: ComputerWithHealthResponseDto) => void;
}> = ({
  computers,
  loading,
  searchTerm,
  statusFilter,
  onComputerClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();

  const filteredComputers = computers?.filter((computer) => {
    const matchesSearch = computer?.computerName
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());
    const normalizedStatus = normalizeStatus(computer?.status);
    const matchesStatus =
      statusFilter === "all" || normalizedStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <ComputerListSection>
        <LoadingContainer>
          <CircularProgress size={40} />
        </LoadingContainer>
      </ComputerListSection>
    );
  }

  if (filteredComputers.length === 0) {
    return (
      <ComputerListSection>
        <ComputerTable>
          <EmptyState>
            <EmptyStateTitle>
              {t(messages.No_Computers_Found())}
            </EmptyStateTitle>
            <EmptyStateText>
              {searchTerm || statusFilter !== "all"
                ? t(messages.Try_adjusting_your_search_or_filters())
                : t(messages.No_computers_have_been_registered_yet())}
            </EmptyStateText>
          </EmptyState>
        </ComputerTable>
      </ComputerListSection>
    );
  }

  return (
    <ComputerListSection>
      <ComputerTable>
        <TableHeader>
          <div>{t(messages.Computer_Name())}</div>
          <HiddenOnMobile>
            <div>{t(messages.Last_Check())}</div>
          </HiddenOnMobile>
          <div>{t(messages.Status())}</div>
          <HiddenOnMobile>
            <div>{t(messages.Time_Elapsed())}</div>
          </HiddenOnMobile>
          <HiddenOnMobile>
            <div>{t(messages.Health())}</div>
          </HiddenOnMobile>
          <div>{t(messages.Actions())}</div>
        </TableHeader>

        {filteredComputers.map((computer) => (
          <TableRow
            key={computer?.id}
            onClick={() => onComputerClick(computer)}
          >
            <ComputerName>{computer?.computerName}</ComputerName>
            <HiddenOnMobile>
              <LastCheckTime>
                {computer?.lastCheckDateTime
                  ? formatDateTime(computer?.lastCheckDateTime)
                  : t(messages.Never())}
              </LastCheckTime>
            </HiddenOnMobile>{" "}
            <div>
              <StatusBadge status={normalizeStatus(computer?.status)}>
                <StatusDot status={normalizeStatus(computer?.status)} />
                {computer?.status}
              </StatusBadge>
            </div>
            <HiddenOnMobile>
              <ElapsedTime>
                {computer?.minutesSinceLastCheck
                  ? formatTimeElapsed(computer?.minutesSinceLastCheck)
                  : t(messages.few_seconds_ago())}
              </ElapsedTime>
            </HiddenOnMobile>
            <HiddenOnMobile>
              <div>{computer?.isCurrentlyHealthy ? "✅" : "❌"}</div>
            </HiddenOnMobile>{" "}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <ActionsButton
                onClick={(e) => {
                  e.stopPropagation();
                  onComputerClick(computer);
                }}
              >
                {t(messages.View_Details())}
              </ActionsButton>
              {onDeleteClick && (
                <ActionsButton
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteClick(computer);
                  }}
                  style={{
                    color: "#dc3545",
                    borderColor: "#dc3545",
                    padding: "6px 8px",
                  }}
                  title={t(messages.Delete_computer())}
                >
                  <Delete style={{ fontSize: "16px" }} />
                </ActionsButton>
              )}
            </div>
          </TableRow>
        ))}
      </ComputerTable>
    </ComputerListSection>
  );
};
